import { useMemo } from "react";
import { VERTICAL_TABS_BASE_GROUPS } from "@miplatform/constants";
import {
  IAttemptListItemResponseForWorkOrder,
  ICalendarWorkTemplateBase,
} from "@miplatform/types";
import { TabGroupItem } from "./CalendarVerticalTabs";

export enum SchedulerViewType {
  Assignment = "assignment",
  Scheduler = "scheduler",
}

const baseTabGroups = {
  Unassigned: {
    label: "Unassigned Work Orders",
    view: SchedulerViewType.Assignment,
    badgeTone: "unassigned" as const,
    disabledIfLocked: true,
  },
  "Not Ready": {
    label: "Not Ready Work Orders",
    view: SchedulerViewType.Assignment,
    badgeTone: "notReady" as const,
    disabledIfLocked: true,
  },
  Assigned: {
    label: "Assigned Work Orders",
    view: SchedulerViewType.Scheduler,
    badgeTone: "assigned" as const,
    disabledIfLocked: false,
  },
} as const satisfies Record<
  (typeof VERTICAL_TABS_BASE_GROUPS)[keyof typeof VERTICAL_TABS_BASE_GROUPS],
  {
    label: string;
    view: SchedulerViewType;
    badgeTone: "unassigned" | "notReady" | "assigned";
    disabledIfLocked: boolean;
  }
>;

export type TabGroupKey = keyof typeof baseTabGroups;

export const DEFAULT_GROUP = "Assigned" satisfies TabGroupKey;

type SchedulerTabGroups = Record<
  TabGroupKey,
  (typeof baseTabGroups)[TabGroupKey] & {
    items: TabGroupItem[];
    disabled: boolean;
  }
>;

/** TODO: replace with product rule when Not Ready criteria is defined */
function getNotReadyWorkOrders(
  _rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[]
): IAttemptListItemResponseForWorkOrder[] {
  return [];
}

function countWorkOrdersForTemplate(
  workOrders: IAttemptListItemResponseForWorkOrder[],
  templateId: string
): number {
  return workOrders.filter((wo) => wo.workOrderTemplateId === templateId)
    .length;
}

export function useNHCPrioritySchedulerTabs(
  workTemplateBases: ICalendarWorkTemplateBase[],
  events: IAttemptListItemResponseForWorkOrder[],
  rawUnassignedWorkOrders: IAttemptListItemResponseForWorkOrder[],
  isCalendarLocked?: boolean
): SchedulerTabGroups {
  const notReadyWorkOrders = useMemo(
    () => getNotReadyWorkOrders(rawUnassignedWorkOrders),
    [rawUnassignedWorkOrders]
  );

  return useMemo(() => {
    return Object.fromEntries(
      (Object.keys(baseTabGroups) as TabGroupKey[]).map((groupKey) => [
        groupKey,
        {
          ...baseTabGroups[groupKey],
          disabled:
            isCalendarLocked && baseTabGroups[groupKey].disabledIfLocked,
          items: workTemplateBases.map((template) => ({
            disabled:
              isCalendarLocked && baseTabGroups[groupKey].disabledIfLocked,
            label: template.name,
            value: template.id,
            count:
              groupKey === VERTICAL_TABS_BASE_GROUPS.UNASSIGNED
                ? countWorkOrdersForTemplate(
                    rawUnassignedWorkOrders,
                    template.id
                  )
                : groupKey === VERTICAL_TABS_BASE_GROUPS.NOT_READY
                ? countWorkOrdersForTemplate(notReadyWorkOrders, template.id)
                : 0,
          })),
        },
      ])
    ) as SchedulerTabGroups;
  }, [
    workTemplateBases,
    isCalendarLocked,
    events,
    rawUnassignedWorkOrders,
    notReadyWorkOrders,
  ]);
}
